import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownItemContent,
  IconButton,
  Switch,
  Tag,
  TextField,
} from '@ads/components-react';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  ClockIcon,
  EllipsisVerticalIcon,
  PencilSquareIcon,
  TrashIcon,
  UserGroupIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '@/shared/ui/molecules/EmptyState';
import {
  EPublicationStatus,
  type ExamPublication,
  type ExamPublishStats,
  type LessonPlan,
} from '../../../types/exam';
import { LessonPlanSearchModal } from '../LessonPlanSearchModal';

export interface PublicationListProps {
  readonly examId: number;
  readonly onEditPublication?: (pub: ExamPublication) => void;
  readonly onExpandDuration?: (pub: ExamPublication) => void;
  readonly onAddStudents?: (pub: ExamPublication) => void;
  readonly onDeletePublication?: (pub: ExamPublication) => void;
  readonly onRemoveStudent?: (publicationId: number, studentId: number) => void;
}

const statusTagMap: Record<EPublicationStatus, 'positive' | 'negative' | 'highlight'> = {
  [EPublicationStatus.Active]: 'positive',
  [EPublicationStatus.Closed]: 'negative',
  [EPublicationStatus.Scheduled]: 'highlight',
};

/* ── Stats Card ───────────────────────────────────────────────────────────── */
function StatsCard({
  stats,
  t,
}: {
  stats: ExamPublishStats;
  t: (k: string, opts?: Record<string, unknown>) => string;
}) {
  return (
    <div className="grid grid-cols-2 gap-xs rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-body p-xxxs sm:grid-cols-4 lg:grid-cols-7">
      <StatItem label={t('publications.stats.totalStudents')} value={String(stats.totalStudents)} />
      <StatItem label={t('publications.stats.completed')} value={String(stats.completedCount)} />
      <StatItem label={t('publications.stats.pending')} value={String(stats.pendingCount)} />
      <StatItem
        label={t('publications.stats.average')}
        value={stats.averageGrade !== null ? stats.averageGrade.toFixed(1) : '—'}
      />
      <StatItem
        label={t('publications.stats.highest')}
        value={stats.highestGrade !== null ? stats.highestGrade.toFixed(1) : '—'}
      />
      <StatItem
        label={t('publications.stats.lowest')}
        value={stats.lowestGrade !== null ? stats.lowestGrade.toFixed(1) : '—'}
      />
      <StatItem
        label={t('publications.stats.pendingCorrection')}
        value={String(stats.pendingCorrectionCount)}
      />
    </div>
  );
}

function StatItem({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-quark">
      <span className="ts-caption-xxs text-ctx-content-placeholder">{label}</span>
      <span className="ts-paragraph-xs font-semibold text-ctx-content-title">{value}</span>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════════ */

export function PublicationList({
  examId,
  onEditPublication,
  onExpandDuration,
  onAddStudents,
  onDeletePublication,
  onRemoveStudent,
}: PublicationListProps) {
  const { t } = useTranslation('examManagement');
  const [showSearch, setShowSearch] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<LessonPlan | null>(null);
  const [maxAttempts, setMaxAttempts] = useState('3');
  const [duration, setDuration] = useState('120');
  const [teacherEval, setTeacherEval] = useState(true);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const publications: readonly ExamPublication[] = [];
  void examId;
  const examDisciplineId: number | null = null;

  const handleSelectPlan = (plans: readonly LessonPlan[]) => {
    const first = plans[0];
    if (first) {
      setSelectedPlan(first);
      setShowSearch(false);
      setShowForm(true);
    }
  };

  const handlePublish = () => {
    if (!selectedPlan) return;
    void maxAttempts;
    void duration;
    void teacherEval;
    setShowForm(false);
    setSelectedPlan(null);
  };

  const toggleExpand = (pubId: number) => {
    setExpandedId(prev => (prev === pubId ? null : pubId));
  };

  return (
    <div className="flex flex-col gap-md">
      <div className="flex items-center justify-between">
        <h3 className="ts-subtitle-sm text-ctx-content-title">{t('publications.title')}</h3>
        <Button hierarchy="primary" size="md" onClick={() => setShowSearch(true)}>
          {t('actions.publishNewLesson')}
        </Button>
      </div>

      {showSearch && (
        <LessonPlanSearchModal
          preselectedDisciplineId={examDisciplineId}
          onSelect={handleSelectPlan}
          onCancel={() => setShowSearch(false)}
        />
      )}

      {showForm && selectedPlan && (
        <div className="flex flex-col gap-mini rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-surface p-md">
          <h4 className="ts-subtitle-sm text-ctx-content-title">{t('publications.form.title')}</h4>
          <div className="flex flex-col gap-xxs">
            <span className="ts-subtitle-xs text-ctx-content-base">
              {t('publications.form.selectedLesson')}
            </span>
            <span className="ts-paragraph-xs text-ctx-content-base">{selectedPlan.title}</span>
            <Button
              hierarchy="secondary"
              size="md"
              onClick={() => {
                setShowForm(false);
                setShowSearch(true);
              }}
            >
              {t('actions.changeLesson')}
            </Button>
          </div>
          <div className="mt-xxs grid grid-cols-1 gap-md md:grid-cols-2">
            <TextField
              label={t('publications.form.maxAttempts')}
              type="number"
              value={maxAttempts}
              onChange={e => setMaxAttempts(e.target.value)}
              size="md"
            />
            <TextField
              label={t('publications.form.duration')}
              type="number"
              value={duration}
              onChange={e => setDuration(e.target.value)}
              size="md"
            />
          </div>
          <Switch
            label={t('publications.teacherEval.label')}
            checked={teacherEval}
            onCheckedChange={(checked: boolean) => setTeacherEval(checked)}
          />
          <div className="flex gap-mini mt-xs">
            <Button hierarchy="primary" size="md" onClick={handlePublish}>
              {t('actions.publish')}
            </Button>
            <Button hierarchy="tertiary" size="md" onClick={() => setShowForm(false)}>
              {t('actions.cancel')}
            </Button>
          </div>
        </div>
      )}

      {publications.length === 0 ? (
        <EmptyState title={t('publications.empty')} />
      ) : (
        <div className="flex flex-col gap-xs">
          {publications.map(pub => {
            const isExpanded = expandedId === pub.id;
            return (
              <div
                key={pub.id}
                className="overflow-hidden rounded-md border-hairline border-ctx-layout-border bg-ctx-layout-surface shadow-level1"
              >
                {/* Publication header */}
                <div className="flex items-center gap-xs p-xs">
                  <button
                    type="button"
                    className="shrink-0 text-ctx-content-brand"
                    onClick={() => toggleExpand(pub.id)}
                  >
                    {isExpanded ? (
                      <ChevronDownIcon className="h-xxxs w-xxxs" />
                    ) : (
                      <ChevronRightIcon className="h-xxxs w-xxxs" />
                    )}
                  </button>

                  <div className="flex flex-1 flex-col gap-nano">
                    <div className="flex flex-wrap items-center gap-nano">
                      <span className="ts-paragraph-xs !font-medium text-ctx-content-title mr-xxs">
                        {pub.lessonPlanTitle}
                      </span>
                      <Tag
                        label={t(`publications.status.${pub.status}` as never)}
                        size="sm"
                        status={statusTagMap[pub.status]}
                        custom={{ text: 'text-nowrap' }}
                      />
                      <Tag
                        label={
                          pub.teacherEvalPublished
                            ? t('publications.teacherEval.published')
                            : t('publications.teacherEval.notPublished')
                        }
                        size="sm"
                        status={pub.teacherEvalPublished ? 'positive' : 'default'}
                        custom={{ text: 'text-nowrap' }}
                      />
                    </div>
                    <span className="ts-caption-xxs text-ctx-content-title">
                      {pub.classGroup} — {pub.professorName} —{' '}
                      {new Date(pub.startDate).toLocaleDateString('pt-BR')} a{' '}
                      {new Date(pub.endDate).toLocaleDateString('pt-BR')}
                    </span>
                  </div>

                  <div className="flex shrink-0 items-center gap-xxs">
                    <span className="ts-subtitle-xs text-ctx-content-base">
                      {pub.stats.completedCount}/{pub.stats.totalStudents}
                    </span>
                    <Dropdown
                      trigger={
                        <IconButton
                          icon={EllipsisVerticalIcon}
                          ariaLabel={t('actions.edit')}
                          hierarchy="tertiary"
                          size="md"
                        />
                      }
                      align="end"
                    >
                      {onEditPublication && (
                        <DropdownItem onClick={() => onEditPublication(pub)}>
                          <DropdownItemContent leadingIcon={PencilSquareIcon}>
                            {t('publications.actions.edit')}
                          </DropdownItemContent>
                        </DropdownItem>
                      )}

                      {onExpandDuration && pub.status !== EPublicationStatus.Closed && (
                        <DropdownItem onClick={() => onExpandDuration(pub)}>
                          <DropdownItemContent leadingIcon={ClockIcon}>
                            {t('publications.actions.expandDuration')}
                          </DropdownItemContent>
                        </DropdownItem>
                      )}

                      {onAddStudents && pub.status !== EPublicationStatus.Closed && (
                        <DropdownItem onClick={() => onAddStudents(pub)}>
                          <DropdownItemContent leadingIcon={UserGroupIcon}>
                            {t('publications.actions.addStudents')}
                          </DropdownItemContent>
                        </DropdownItem>
                      )}

                      {onDeletePublication && (
                        <DropdownItem onClick={() => onDeletePublication(pub)}>
                          <DropdownItemContent leadingIcon={TrashIcon}>
                            {t('publications.actions.delete')}
                          </DropdownItemContent>
                        </DropdownItem>
                      )}
                    </Dropdown>
                  </div>
                </div>

                {/* Expanded content: stats + students */}
                {isExpanded && (
                  <div className="flex flex-col gap-xs border-t-hairline border-ctx-layout-border bg-ctx-layout-body p-xs">
                    <StatsCard
                      stats={pub.stats}
                      t={t as (k: string, opts?: Record<string, unknown>) => string}
                    />

                    {pub.students.length === 0 ? (
                      <p className="ts-paragraph-xs text-ctx-content-base py-xs text-center">
                        {t('publications.students.noStudents')}
                      </p>
                    ) : (
                      <div className="flex flex-col gap-xxs">
                        {/* Student list header */}
                        <div className="grid grid-cols-7 gap-xs px-xs py-none">
                          <span className="col-span-2 ts-caption-xxs font-semibold text-ctx-content-placeholder">
                            {t('publications.students.name')}
                          </span>
                          <span className="ts-caption-xxs font-semibold text-ctx-content-placeholder">
                            {t('publications.students.cpf')}
                          </span>
                          <span className="ts-caption-xxs font-semibold text-ctx-content-placeholder">
                            {t('publications.students.attempts')}
                          </span>
                          <span className="ts-caption-xxs font-semibold text-ctx-content-placeholder">
                            {t('publications.students.bestGrade')}
                          </span>
                          <span className="ts-caption-xxs font-semibold text-ctx-content-placeholder">
                            {t('publications.students.lastAttempt')}
                          </span>
                          <span className="ts-caption-xxs font-semibold text-ctx-content-placeholder" />
                        </div>

                        {pub.students.map(student => (
                          <div
                            key={student.id}
                            className="grid grid-cols-7 items-center gap-xs rounded-sm bg-ctx-layout-surface px-xs py-xxs"
                          >
                            <span className="col-span-2 ts-paragraph-xs text-ctx-content-title">
                              {student.personName}
                            </span>
                            <span className="ts-caption-xxs text-ctx-content-title">
                              {student.personCpf}
                            </span>
                            <span className="ts-caption-xxs text-ctx-content-base">
                              {student.attempts}
                            </span>
                            <span className="ts-caption-xxs text-ctx-content-base">
                              {student.bestGrade !== null ? student.bestGrade.toFixed(1) : '—'}
                            </span>
                            <span className="ts-caption-xxs text-ctx-content-title">
                              {student.lastAttemptDate
                                ? new Date(student.lastAttemptDate).toLocaleDateString('pt-BR')
                                : '—'}
                            </span>
                            <div className="flex justify-end">
                              {onRemoveStudent && (
                                <button
                                  type="button"
                                  className="flex items-center gap-quark ts-caption-xxs text-negative-500 hover:underline"
                                  onClick={() => onRemoveStudent(pub.id, student.id)}
                                >
                                  <XCircleIcon className="h-xxxs w-xxxs" />
                                  {t('publications.actions.removeStudent')}
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
